/**
 * Open Plaid Link and hand back the connection it made, or null if the
 * operator closed it.
 *
 * Plaid's script is loaded ONLY here, on the Connect tap — never on page load
 * — so the one third-party script in the app runs only when it is needed.
 * In local dev and the browser suite the server answers `mode: 'fake'` and
 * the script is skipped entirely: the fake bank accepts a fixed token.
 */

import { bankApi, connectionApi, type ConnectionStatus } from './api';

const SCRIPT = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
const FAKE_PUBLIC_TOKEN = 'fake-public-token';

interface PlaidHandler {
  open: () => void;
  destroy: () => void;
}
interface PlaidGlobal {
  create: (opts: {
    token: string;
    onSuccess: (publicToken: string) => void;
    onExit: (err: unknown) => void;
  }) => PlaidHandler;
}

function loadScript(): Promise<PlaidGlobal> {
  const w = window as unknown as { Plaid?: PlaidGlobal };
  if (w.Plaid) return Promise.resolve(w.Plaid);
  return new Promise((resolve, reject) => {
    const el = document.createElement('script');
    el.src = SCRIPT;
    el.async = true;
    el.onload = () => (w.Plaid ? resolve(w.Plaid) : reject(new Error('Plaid did not load')));
    el.onerror = () => reject(new Error("Couldn't reach Plaid. Check your connection."));
    document.head.append(el);
  });
}

/** Open Link with a token; the public token on success, or null if closed. */
async function openLink(linkToken: string): Promise<string | null> {
  const Plaid = await loadScript();
  return new Promise<string | null>((resolve) => {
    const handler = Plaid.create({
      token: linkToken,
      onSuccess: (pt) => {
        handler.destroy();
        resolve(pt);
      },
      onExit: () => {
        handler.destroy();
        resolve(null);
      },
    });
    handler.open();
  });
}

export async function connectBank(): Promise<{ institutionName: string | null } | null> {
  const { linkToken, mode } = await bankApi.linkToken();
  if (mode === 'fake') return bankApi.exchange(FAKE_PUBLIC_TOKEN);
  const publicToken = await openLink(linkToken);
  return publicToken ? bankApi.exchange(publicToken) : null;
}

/**
 * Repair an existing connection (Plaid UPDATE MODE, v7.1.0). The link token is
 * minted server-side from the stored access token, which never reaches the
 * browser. On success there is NO public token to exchange — the connection
 * keeps its access token — so the server is told it finished, marks it healthy
 * and syncs it once; if the bank still refuses, the status it returns says so.
 * Resolves with the connection's status afterwards, or null if Link was closed.
 */
export async function reconnectBank(itemId: string): Promise<ConnectionStatus | null> {
  const { linkToken, mode } = await connectionApi.reconnectToken(itemId);
  if (mode !== 'fake' && (await openLink(linkToken)) == null) return null;
  return (await connectionApi.reconnected(itemId)).status;
}
